import { Component, ErrorInfo, ReactNode } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error reporting service
      // errorReportingService.captureException(error, { extra: errorInfo });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleContactSupport = () => {
    const errorDetails = `
Error Boundary Report:
- Error: ${this.state.error?.message}
- Stack: ${this.state.error?.stack}
- Component Stack: ${this.state.errorInfo?.componentStack}
- Timestamp: ${new Date().toISOString()}
- URL: ${window.location.href}
- User Agent: ${navigator.userAgent}
    `;

    const subject = encodeURIComponent('Application Error Report');
    const body = encodeURIComponent(`
Hello Support Team,

I encountered an unexpected error in the application. Please find the details below:

${errorDetails}

Please help me resolve this issue.

Thank you
    `);

    const mailtoLink = `mailto:support@insights.ps?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          backgroundColor: '#f8f9fa'
        }}>
          <Card style={{ maxWidth: '600px', width: '100%' }}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                width: '4rem',
                height: '4rem',
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem auto'
              }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626', fontSize: '2rem' }}></i>
              </div>

              <h2 style={{
                fontSize: '1.5rem',
                margin: '0 0 1rem 0',
                color: '#1f2937'
              }}>
                Something went wrong
              </h2>

              <p style={{
                color: '#6b7280',
                margin: '0 0 1.5rem 0',
                lineHeight: 1.5
              }}>
                We're sorry, but something unexpected happened. The error has been logged and our team will investigate.
              </p>

              <Message
                severity="error"
                text={this.state.error?.message || 'An unexpected error occurred'}
                style={{ marginBottom: '1.5rem', textAlign: 'left' }}
              />

              <div style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <Button
                  label="Reload Page"
                  icon="pi pi-refresh"
                  onClick={this.handleReload}
                />
                <Button
                  label="Go Home"
                  icon="pi pi-home"
                  onClick={this.handleGoHome}
                  outlined
                />
                <Button
                  label="Contact Support"
                  icon="pi pi-envelope"
                  onClick={this.handleContactSupport}
                  outlined
                />
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details style={{
                  marginTop: '2rem',
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 500, marginBottom: '0.5rem' }}>
                    Error Details (Development Mode)
                  </summary>
                  <div style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    <strong>Error:</strong> {this.state.error.message}
                    {'\n\n'}
                    <strong>Stack Trace:</strong>
                    {'\n'}
                    {this.state.error.stack}
                    {this.state.errorInfo?.componentStack && (
                      <>
                        {'\n\n'}
                        <strong>Component Stack:</strong>
                        {'\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </div>
                </details>
              )}
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;